import { useState, useEffect } from 'react'

interface WeatherData {
  temp: number
  condition: string
  humidity: number
  precipitation?: number
  forecast: DailyForecast[]
}

interface DailyForecast {
  date: Date
  high: number
  low: number
  condition: string
  precipitation: number
}

interface WeatherAlert {
  type: 'frost' | 'heat' | 'rain' | 'wind'
  message: string
  severity: 'low' | 'medium' | 'high'
  date: Date
}

export const useWeather = (zipCode: string | undefined) => {
  const [weather, setWeather] = useState<WeatherData | null>(null)
  const [alerts, setAlerts] = useState<WeatherAlert[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!zipCode) return

    const fetchWeather = async () => {
      setLoading(true)
      setError(null)

      try {
        // Mock weather data - replace with OpenWeatherMap API
        // API: https://api.openweathermap.org/data/2.5/forecast?zip={zip},us&appid={key}
        
        const mockWeather: WeatherData = {
          temp: 72,
          condition: 'Partly Cloudy',
          humidity: 65,
          forecast: generateMockForecast()
        }

        setWeather(mockWeather)
        setAlerts(generateMockAlerts())
      } catch (err) {
        setError('Failed to fetch weather data')
      } finally {
        setLoading(false)
      }
    }

    fetchWeather()
    
    // Refresh every 30 minutes
    const interval = setInterval(fetchWeather, 30 * 60 * 1000)
    return () => clearInterval(interval)
  }, [zipCode])

  return { weather, alerts, loading, error }
}

const generateMockForecast = (): DailyForecast[] => {
  const forecast: DailyForecast[] = []
  const today = new Date()
  
  for (let i = 0; i < 7; i++) {
    const date = new Date(today)
    date.setDate(date.getDate() + i)
    
    forecast.push({
      date,
      high: 70 + Math.floor(Math.random() * 20),
      low: 50 + Math.floor(Math.random() * 15),
      condition: ['Sunny', 'Partly Cloudy', 'Cloudy', 'Rain'][Math.floor(Math.random() * 4)],
      precipitation: Math.floor(Math.random() * 50)
    })
  }
  
  return forecast
}

const generateMockAlerts = (): WeatherAlert[] => {
  const alerts: WeatherAlert[] = []
  const today = new Date()
  
  // Random frost warning
  if (Math.random() > 0.7) {
    alerts.push({
      type: 'frost',
      message: 'Frost warning tonight! Cover tender plants.',
      severity: 'high',
      date: today
    })
  }
  
  // Random heat warning
  if (Math.random() > 0.8) {
    alerts.push({
      type: 'heat',
      message: 'High heat expected. Water plants deeply in morning.',
      severity: 'medium',
      date: today
    })
  }
  
  return alerts
}

// Real API integration (when ready):
/*
const fetchRealWeather = async (zipCode: string) => {
  const API_KEY = process.env.REACT_APP_WEATHER_API_KEY
  const response = await fetch(
    `https://api.openweathermap.org/data/2.5/forecast?zip=${zipCode},us&appid=${API_KEY}&units=imperial`
  )
  const data = await response.json()
  
  return {
    temp: Math.round(data.list[0].main.temp),
    condition: data.list[0].weather[0].main,
    humidity: data.list[0].main.humidity,
    forecast: data.list.slice(0, 7).map((day: any) => ({
      date: new Date(day.dt * 1000),
      high: Math.round(day.main.temp_max),
      low: Math.round(day.main.temp_min),
      condition: day.weather[0].main,
      precipitation: day.pop * 100
    }))
  }
}
*/

export default useWeather
